import { X, Printer } from "lucide-react";
import { createPortal } from "react-dom";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

export default function MonthlyPrintReport({ data, monthName, onClose }) {
  const handlePrint = () => window.print();
  const { year, month, days_count, days, products, materials } = data;

  return (
    <>
      <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-neutral-900">Raport Lunar · {monthName} {year}</h3>
              <p className="text-xs text-neutral-500 mt-0.5">{days_count} zile · {products.length} produse · {materials.length} materii prime</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="print-monthly-confirm"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
                Tipărește
              </button>
              <button
                data-testid="print-monthly-close"
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-900 p-1 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto thin-scroll p-6 bg-neutral-100">
            <div className="bg-white shadow-sm mx-auto" style={{ maxWidth: "210mm", padding: "12mm" }}>
              <Body year={year} month={month} monthName={monthName} days={days} products={products} materials={materials} days_count={days_count} />
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <div className="hidden print:block print-area">
          <Body year={year} month={month} monthName={monthName} days={days} products={products} materials={materials} days_count={days_count} />
        </div>,
        document.body
      )}
    </>
  );
}

function Body({ year, monthName, days, days_count, products, materials }) {
  const totalConsumption = materials.reduce((s, m) => s + (m.consumption || 0), 0);
  const totalEntries = materials.reduce((s, m) => s + (m.entries || 0), 0);

  return (
    <div className="text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-end justify-between mb-6 pb-3 border-b-2 border-neutral-900 print-avoid-break">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Raport Lunar Producție</h1>
          <p className="text-xs text-neutral-600 mt-0.5">Sumar agregat al rapoartelor zilnice</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-neutral-500 uppercase tracking-wider">Perioada</p>
          <p className="text-sm font-semibold">{monthName} {year}</p>
          <p className="text-[10px] text-neutral-500 mt-0.5">{days_count} zile cu raport</p>
        </div>
      </div>

      <div className="mb-5">
        <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
          Total produse fabricate ({products.length})
        </h2>
        <table className="print-table w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="border border-neutral-700 px-2 py-1 text-left w-8">#</th>
              <th className="border border-neutral-700 px-2 py-1 text-left">Produs</th>
              <th className="border border-neutral-700 px-2 py-1 text-right w-32">Cantitate totală</th>
              <th className="border border-neutral-700 px-2 py-1 text-left w-16">U.M.</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, idx) => (
              <tr key={idx}>
                <td className="border border-neutral-400 px-2 py-1 text-neutral-500">{idx + 1}</td>
                <td className="border border-neutral-400 px-2 py-1 font-medium">{p.name}</td>
                <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-semibold">{fmt(p.quantity)}</td>
                <td className="border border-neutral-400 px-2 py-1 text-neutral-600">{p.unit}</td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={4} className="border border-neutral-400 px-2 py-3 text-center text-neutral-500">Niciun produs</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
          Materii prime · sumar lunar ({materials.length})
        </h2>
        <table className="print-table w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="border border-neutral-700 px-2 py-1 text-left w-8">#</th>
              <th className="border border-neutral-700 px-2 py-1 text-left">Materie primă</th>
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Stoc inițial luna</th>
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Total intrări</th>
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Total consum</th>
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Stoc final luna</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m, idx) => (
              <tr key={m.name}>
                <td className="border border-neutral-400 px-2 py-1 text-neutral-500">{idx + 1}</td>
                <td className="border border-neutral-400 px-2 py-1 capitalize">{m.name}</td>
                <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{fmt(m.initial_stock)}</td>
                <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{fmt(m.entries)}</td>
                <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-semibold">{fmt(m.consumption)}</td>
                <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-bold">{fmt(m.final_stock)}</td>
              </tr>
            ))}
            {materials.length === 0 && (
              <tr>
                <td colSpan={6} className="border border-neutral-400 px-2 py-3 text-center text-neutral-500">Nicio materie primă</td>
              </tr>
            )}
          </tbody>
          {materials.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3} className="border border-neutral-700 px-2 py-1 font-bold text-right uppercase tracking-wider">Total (kg)</td>
                <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(totalEntries)}</td>
                <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(totalConsumption)}</td>
                <td className="border border-neutral-700 px-2 py-1"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {days && days.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] text-neutral-500">
            <span className="font-semibold uppercase tracking-wider">Zile incluse:</span> {days.join(", ")}
          </p>
        </div>
      )}

      <div className="mt-8 pt-4 border-t border-neutral-300 grid grid-cols-3 gap-6 text-xs text-neutral-600">
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Întocmit</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Verificat</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Aprobat</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
      </div>
    </div>
  );
}
